import React, { useCallback, useMemo, useState } from 'react';
import { Spinner } from '@/components/ui/spinner';
import { Col, Form, Row, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Button from '../../../../../components/button';
import { FormGroup } from '../../../../../components/form/FormGroup';
import { useCurrencyFormatter } from '../../../../../hooks/useCurrencyFormatter';
import { formatDateShort } from '../../../../../utils/dateUtils';
import {
  ApprovalRequestStatus,
  ExpenseGroup,
  ExpenseStatus,
  useExpenseApprovalRequestsQuery,
  useUpdateExpenseApprovalRequestStatusMutation,
} from '../../../__generated__/graphql';
import { ExpenseStatusCell } from '../Common/ExpenseStatusCell';
import {
  DocumentFile,
  Expense,
  ExpenseStatusInfo,
  ExpenseGroupStatusInfo,
} from '../types';
import FlairIcon from '../../../../../atomic/atoms/FlairIcon';
import {
  ExtraFieldsSupportedObject,
  ExtraFieldsViewArea,
} from '../../../components/ExtraFields';
import { ExtraFieldsFormId } from '../../../components/ExtraFields/types';
import { useUserInfo } from '../../../context/UserInfo';
import { ApprovalRequestCommentModal } from '../../ApprovalRequests/ApprovalRequestsTable/ApprovalRequestCommentModal';
import FormattedTime from '../../../../../components/time/FormattedTime';
import moment from 'moment';

type Props = {
  expense: Expense;
  isManager: boolean;
  onStatusChange?: (info: ExpenseStatusInfo) => Promise<void>;
};

const EMPTY_VALUE = '---';

export const ExpenseFields: React.FC<React.PropsWithChildren<Props>> = ({
  expense,
  isManager,
  onStatusChange,
}) => {
  const { t } = useTranslation();
  const readonly = true;
  const id = expense.id;
  const formatCurrency = useCurrencyFormatter();

  const statusInfo: ExpenseStatusInfo = useMemo(
    () => ({
      expenseId: id,
      status: expense.status,
      declineReason: null,
    }),
    [expense.status, id],
  );

  return (
    <>
      {expense.description && (
        <FormGroup>
          <Form.Label className="fw-bold">
            {t('expenses.expenseSideBar.expenseDescription')}
          </Form.Label>
          <p>{expense.description}</p>
        </FormGroup>
      )}
      <Row>
        <Col>
          <FormGroup>
            <div className="d-flex align-items-center gap-1">
              <Form.Label className="fw-bold">
                {t('expenses.expenseSideBar.finalAmount')}
              </Form.Label>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id="final-amount-tooltip">
                    {t('expenses.expenseSideBar.finalAmountTooltip')}
                  </Tooltip>
                }>
                <span className="ms-1 mt-n1">
                  <FlairIcon icon="information-circle-outline" size="sm" />
                </span>
              </OverlayTrigger>
            </div>

            <p>
              {expense.finalAmount != null
                ? formatCurrency({
                    // TODO: amount is deprecated, remove it in future
                    value: expense.amount + expense.finalAmount,
                    currency: expense.currencyIsoCode,
                  })
                : EMPTY_VALUE}
            </p>
          </FormGroup>
        </Col>
        {expense.merchant && (
          <Col>
            <FormGroup>
              <Form.Label className="fw-bold">
                {t('expenses.expenseSideBar.merchant')}
              </Form.Label>
              <p>{expense.merchant}</p>
            </FormGroup>
          </Col>
        )}
      </Row>

      <Row>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('expenses.expenseSideBar.category')}
            </Form.Label>
            <p>{expense.category?.Name ?? EMPTY_VALUE}</p>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('expenses.expenseSideBar.dateFrom')}
            </Form.Label>
            <p>
              {expense.dateFrom
                ? formatDateShort(expense.dateFrom)
                : EMPTY_VALUE}
            </p>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('expenses.expenseSideBar.dateTo')}
            </Form.Label>
            <p>
              {expense.dateTo ? formatDateShort(expense.dateTo) : EMPTY_VALUE}
            </p>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('expenses.expenseSideBar.timeFrom')}
            </Form.Label>
            <p>
              {expense.timeFrom ? (
                <FormattedTime time={moment(expense.timeFrom, 'HH:mm')!} />
              ) : (
                EMPTY_VALUE
              )}
            </p>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('expenses.expenseSideBar.timeTo')}
            </Form.Label>
            <p>
              {expense.timeTo ? (
                <FormattedTime time={moment(expense.timeTo, 'HH:mm')!} />
              ) : (
                EMPTY_VALUE
              )}
            </p>
          </FormGroup>
        </Col>
      </Row>

      <Row>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('expenses.expenseSideBar.project')}
            </Form.Label>
            <p>{expense.project ? expense.project : EMPTY_VALUE}</p>
          </FormGroup>
        </Col>
        <Col>
          <FormGroup>
            <Form.Label className="fw-bold">
              {t('expenses.expenseSideBar.status')}
            </Form.Label>
            <ExpenseStatusCell
              readonly={readonly}
              onChange={onStatusChange}
              updatingToStatus={null}
              value={statusInfo}
            />
          </FormGroup>
        </Col>
      </Row>
      {expense.documents.length > 0 ? (
        <Row>
          <Col>
            <FormGroup>
              <Form.Label className="fw-bold">
                {t('expenses.expenseSideBar.invoiceAttached')}
              </Form.Label>
              {expense.documents.map((document: DocumentFile) => (
                <div key={document.id}>
                  <a
                    target="_blank"
                    rel="noreferrer"
                    href={document.downloadPublicUrl}>
                    {document.fileName}
                    <span className="ms-2">
                      <FlairIcon size="xs" icon="external-link-outline" />
                    </span>
                  </a>
                </div>
              ))}
            </FormGroup>
          </Col>
        </Row>
      ) : (
        ''
      )}

      {isManager && (
        <Row>
          <Col>
            <FormGroup>
              <Form.Label className="fw-bold">
                {t('expenses.expenseSideBar.requestedBy')}
              </Form.Label>
              <p>{expense.employee.Name}</p>
            </FormGroup>
          </Col>
        </Row>
      )}

      {expense.declineReason && (
        <Row>
          <Col>
            <FormGroup>
              <Form.Label className="fw-bold">
                {t('expenses.expenseSideBar.declineReason')}
              </Form.Label>
              <p>{expense.declineReason}</p>
            </FormGroup>
          </Col>
        </Row>
      )}

      <ExtraFieldsViewArea
        objectApiName={ExtraFieldsSupportedObject.Expense}
        formId={ExtraFieldsFormId.ExpensesDetails}
        recordId={expense.id}
        columnsCount={2}
      />

      <ExpenseActions
        isGroup={false}
        expenseOrGroup={expense}
        isManager={isManager}
        statusInfo={statusInfo}
        onStatusChange={onStatusChange}
      />
    </>
  );
};

type ExpenseActionsProps = {
  isGroup: boolean;
  expenseOrGroup: Expense | ExpenseGroup;
  isManager: boolean;
  onStatusChange?: (info: ExpenseStatusInfo) => Promise<void>;
  onGroupStatusChange?: (info: ExpenseGroupStatusInfo) => Promise<void>;
  statusInfo: ExpenseStatusInfo;
  groupStatusInfo?: ExpenseGroupStatusInfo;
};

export const ExpenseActions: React.FC<
  React.PropsWithChildren<ExpenseActionsProps>
> = ({
  isGroup,
  expenseOrGroup,
  isManager,
  onStatusChange,
  onGroupStatusChange,
  statusInfo,
  groupStatusInfo,
}) => {
  const { t } = useTranslation();
  const { id: currentEmployeeId } = useUserInfo();
  const { data: requests, loading: loadingRequests } =
    useExpenseApprovalRequestsQuery({
      variables: {
        relatedRecordId: expenseOrGroup.Id,
      },
    });

  const [updateExpenseApprovalRequestStatus] =
    useUpdateExpenseApprovalRequestStatusMutation();

  const [savingExpenseStatus, setSavingExpenseStatus] =
    useState<ExpenseStatus | null>(null);

  const [savingApprovalRequestStatus, setSavingApprovalRequestStatus] =
    useState<ApprovalRequestStatus | null>(null);

  const [rejectingModalId, setRejectingModalId] = useState<string | null>(null);

  const handleExpenseStatusChange = useCallback(
    (status: ExpenseStatus) => {
      if (!onStatusChange) {
        return;
      }
      setSavingExpenseStatus(status);

      onStatusChange({ ...statusInfo, status }).then(() => {
        setSavingExpenseStatus(null);
      });
    },
    [onStatusChange, statusInfo],
  );

  const handleGroupStatusChange = useCallback(
    (status: ExpenseStatus) => {
      if (!isGroup || !onGroupStatusChange || !groupStatusInfo) {
        return;
      }
      setSavingExpenseStatus(status);
      onGroupStatusChange({ ...groupStatusInfo, status }).then(() => {
        setSavingExpenseStatus(null);
      });
    },
    [isGroup, onGroupStatusChange, groupStatusInfo],
  );

  const handleApprovalRequestStatusChange = useCallback(
    (
      approvalRequestId: string,
      status: ApprovalRequestStatus,
      comment?: string,
    ) => {
      setSavingApprovalRequestStatus(status);

      updateExpenseApprovalRequestStatus({
        variables: {
          input: {
            id: approvalRequestId,
            status,
            comment: comment || '',
          },
          expenseId: expenseOrGroup.Id,
        },
        awaitRefetchQueries: true,
      }).then(() => {
        setSavingApprovalRequestStatus(null);
      });
    },
    [updateExpenseApprovalRequestStatus, expenseOrGroup.Id],
  );

  const handleReject = useCallback(
    (requestId: string, reason: string | null) => {
      setRejectingModalId(null);
      handleApprovalRequestStatusChange(
        requestId,
        ApprovalRequestStatus.Rejected,
        reason || '',
      );
    },
    [handleApprovalRequestStatusChange],
  );

  const handleRejectButtonClick = useCallback((requestId: string) => {
    setRejectingModalId(requestId);
  }, []);

  const onModalClose = useCallback(() => {
    setRejectingModalId(null);
  }, []);

  if (loadingRequests) {
    return <Spinner />;
  }

  const myRequest = requests?.approvalRequests.find(
    (request) => request.flair__Approver__c === currentEmployeeId,
  );
  const isMyRequestPending =
    myRequest?.flair__Approval_Status__c === ApprovalRequestStatus.Pending;

  const moreThanOneRequest = (requests?.approvalRequests?.length || 0) > 1;

  const anyOtherPendingRequest = requests?.approvalRequests.some(
    (request) =>
      request.flair__Approver__c !== currentEmployeeId &&
      request.flair__Approval_Status__c === ApprovalRequestStatus.Pending,
  );

  if (
    !isManager ||
    expenseOrGroup.flair__Status__c === ExpenseStatus.Reimbursed ||
    expenseOrGroup.flair__Status__c === ExpenseStatus.Declined
  ) {
    return null;
  }

  return (
    <>
      {!!myRequest && !isMyRequestPending && moreThanOneRequest && (
        <>
          {myRequest.flair__Approval_Status__c ===
            ApprovalRequestStatus.Approved && (
            <div className="text-success mb-2">
              {t('expenses.expenseSideBar.alreadyApproved')}
            </div>
          )}
          {myRequest.flair__Approval_Status__c ===
            ApprovalRequestStatus.Rejected && (
            <div className="text-danger mb-2">
              {t('expenses.expenseSideBar.alreadyRejected')}
            </div>
          )}
        </>
      )}

      {!isMyRequestPending && anyOtherPendingRequest && (
        <div className="text-warning mb-2">
          {t('expenses.expenseSideBar.otherPendingRequest')}
        </div>
      )}
      <div className="d-flex">
        {myRequest && isMyRequestPending && (
          <>
            <Button
              label={t('expenses.buttons.decline')}
              isProcessing={
                savingApprovalRequestStatus === ApprovalRequestStatus.Rejected
              }
              disabled={!!savingApprovalRequestStatus}
              size="sm"
              className="me-2"
              variant="outline-danger"
              onClick={() => {
                handleRejectButtonClick(myRequest.Id);
              }}
            />
            {expenseOrGroup.flair__Status__c !== ExpenseStatus.Approved && (
              <Button
                label={t('expenses.buttons.approve')}
                isProcessing={
                  savingApprovalRequestStatus === ApprovalRequestStatus.Approved
                }
                disabled={!!savingApprovalRequestStatus}
                size="sm"
                className="me-2"
                variant="outline-primary"
                onClick={() => {
                  handleApprovalRequestStatusChange(
                    myRequest.Id,
                    ApprovalRequestStatus.Approved,
                  );
                }}
              />
            )}
          </>
        )}

        {isManager &&
          (expenseOrGroup.flair__Status__c === ExpenseStatus.Approved ||
            expenseOrGroup.flair__Status__c === ExpenseStatus.Pending) &&
          (isGroup || !(expenseOrGroup as Expense).flair__Expense_Group__c) && (
            <Button
              size="sm"
              label={t('expenses.buttons.reimbursed')}
              disabled={!!savingExpenseStatus}
              isProcessing={savingExpenseStatus === ExpenseStatus.Reimbursed}
              onClick={() => {
                if (isGroup) {
                  handleGroupStatusChange(ExpenseStatus.Reimbursed);
                } else {
                  handleExpenseStatusChange(ExpenseStatus.Reimbursed);
                }
              }}
            />
          )}
      </div>

      <ApprovalRequestCommentModal
        requestId={rejectingModalId}
        inProgress={
          savingApprovalRequestStatus === ApprovalRequestStatus.Rejected
        }
        onClose={onModalClose}
        onRejected={handleReject}
      />
    </>
  );
};
